import { format, subDays } from "date-fns";
import { useCallback, useEffect, useSyncExternalStore } from "react";

import { apiGet, apiPost, mockFor } from "./client";
import type { AlertRule, AlertRuleProposal, AlertRuleScope } from "./types";

/**
 * Propose/review store for alert configuration.
 *
 * Still an external store rather than react-query, deliberately: the page reads
 * `activeRules` and `proposals` together and mutates them in pairs (approving a
 * proposal creates a rule), so a single snapshot that always agrees with itself is
 * worth more here than per-query caching. The consumer signature is unchanged —
 * `{ activeRules, proposals, propose, review }` — so ManageAlerts.tsx did not have
 * to change when this went live.
 *
 * In LIVE mode the store hydrates from `GET /alert-rules` and pushes every mutation
 * through the API before re-hydrating, so what is on screen is what the database
 * holds. In MOCK mode it keeps the seeded in-memory behaviour.
 *
 * Nothing here fires an alert — Superset does that and writes `lastTriggeredAt`.
 */

/** Resolved once at module load so hook order stays stable, as office-mapping.ts does. */
const LIVE = !mockFor("/alert-rules");

export interface AlertRuleProposalInput {
  alertRuleId: string | null;
  scope: AlertRuleScope;
  officeScope: "all" | string[];
  notificationRecipients: string[];
  active: boolean;
  reason: string;
}

interface StoreState {
  activeRules: AlertRule[];
  proposals: AlertRuleProposal[];
  /** Last mutation or fetch failure, so the page can show why nothing happened. */
  error: string | null;
  /** True while a live fetch or mutation is in flight. */
  isPending: boolean;
}

/** Wire shape of GET /alert-rules and the two mutation responses. */
interface AlertRulesSnapshot {
  rules: AlertRule[];
  proposals: AlertRuleProposal[];
}

function iso(daysAgo: number): string {
  return format(subDays(new Date(), daysAgo), "yyyy-MM-dd'T'HH:mm:ss");
}

function seed(): StoreState {
  const activeRules: AlertRule[] = [
    {
      alertRuleId: "ALR-001",
      scope: { type: "policy_rule", ruleId: "A1", thresholdDaysUnreconciled: 3 },
      officeScope: "all",
      notificationRecipients: ["credit-leadership@athena.example", "dan@athena.example"],
      active: true,
      lastTriggeredAt: iso(3),
    },
    {
      alertRuleId: "ALR-002",
      scope: { type: "policy_rule", ruleId: "A3", thresholdDaysUnreconciled: 5 },
      officeScope: ["Dallas"],
      notificationRecipients: ["dallas-uw@athena.example"],
      active: true,
      lastTriggeredAt: iso(11),
    },
    {
      alertRuleId: "ALR-003",
      scope: { type: "policy_rule", ruleId: "A4", thresholdDaysUnreconciled: 7 },
      officeScope: "all",
      notificationRecipients: ["ops-review@athena.example"],
      active: true,
      lastTriggeredAt: null,
    },
    {
      alertRuleId: "ALR-004",
      scope: { type: "policy_rule", ruleId: "C1", thresholdDaysUnreconciled: 10 },
      officeScope: ["Dallas"],
      notificationRecipients: [
        "credit-leadership@athena.example",
        "portfolio-risk@athena.example",
        "mike@athena.example",
      ],
      active: true,
      lastTriggeredAt: null,
    },
    {
      alertRuleId: "ALR-005",
      scope: { type: "agreement_rate", segment: "small_ticket", thresholdPct: 75 },
      officeScope: "all",
      notificationRecipients: ["credit-leadership@athena.example"],
      active: true,
      lastTriggeredAt: iso(28),
    },
  ];

  const proposals: AlertRuleProposal[] = [
    {
      proposalId: "PRP-101",
      status: "PENDING",
      alertRuleId: null,
      scope: { type: "policy_rule", ruleId: "B1b", thresholdDaysUnreconciled: 14 },
      officeScope: "all",
      notificationRecipients: ["credit-leadership@athena.example"],
      active: true,
      reason:
        "B1b carries the largest candidate volume of any rule. A 14-day trip-wire keeps the reconciliation backlog visible without alerting on every PASS-score approval.",
      proposedByRole: "Credit Policy",
      reviewedAt: null,
      reviewNotes: null,
    },
    {
      proposalId: "PRP-102",
      status: "PENDING",
      alertRuleId: "ALR-002",
      scope: { type: "policy_rule", ruleId: "A3", thresholdDaysUnreconciled: 2 },
      officeScope: ["Dallas"],
      notificationRecipients: ["dallas-uw@athena.example", "credit-leadership@athena.example"],
      active: true,
      reason:
        "Booking over approval is a funding-desk correction, not a credit debate — tighten from 5 days to 2 and copy credit leadership.",
      proposedByRole: "Underwriting Manager",
      reviewedAt: null,
      reviewNotes: null,
    },
  ];

  return { activeRules, proposals, error: null, isPending: false };
}

// In LIVE mode the seeded rules are fiction with real-looking recipient addresses, so the
// store starts empty and hydrates from the API instead. Seeding them and then replacing them
// would flash invented alert rules on screen before the first fetch lands.
let state: StoreState = LIVE
  ? { activeRules: [], proposals: [], error: null, isPending: true }
  : seed();
let counter = 200;
let hydrated = false;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): StoreState {
  return state;
}

function applySnapshot(snapshot: AlertRulesSnapshot) {
  state = {
    activeRules: snapshot.rules,
    proposals: snapshot.proposals,
    error: null,
    isPending: false,
  };
  emit();
}

function fail(error: unknown) {
  state = { ...state, error: (error as Error)?.message ?? String(error), isPending: false };
  emit();
}

/** Fetch the live snapshot. Called once on first mount and after every mutation. */
async function hydrate() {
  if (!LIVE) return;
  state = { ...state, isPending: true };
  emit();
  try {
    applySnapshot(await apiGet<AlertRulesSnapshot>("/alert-rules"));
  } catch (error) {
    fail(error);
  }
}

/**
 * Both mutations return the full post-change snapshot, so the store replaces its state
 * from the response rather than re-fetching. That is not just a saved round trip: an
 * approval creates a rule AND resolves a proposal, and applying the server's own view of
 * both at once is the only way the two cannot briefly disagree on screen.
 */
async function proposeLive(input: AlertRuleProposalInput) {
  state = { ...state, isPending: true, error: null };
  emit();
  try {
    applySnapshot(
      await apiPost<AlertRulesSnapshot>("/alert-rules/propose", {
        alertRuleId: input.alertRuleId,
        scope: input.scope,
        officeScope: input.officeScope,
        notificationRecipients: input.notificationRecipients,
        active: input.active,
        reason: input.reason,
      }),
    );
  } catch (error) {
    fail(error);
  }
}

async function reviewLive(
  proposalId: string,
  decision: "APPROVED" | "REJECTED",
  notes: string,
) {
  state = { ...state, isPending: true, error: null };
  emit();
  try {
    // The review response carries `rules` and `proposals` alongside its own result fields,
    // so the same applySnapshot works for both mutations.
    applySnapshot(
      await apiPost<AlertRulesSnapshot>(
        `/alert-rules/${encodeURIComponent(proposalId)}/review`,
        { decision, notes },
      ),
    );
  } catch (error) {
    // Surfaced rather than swallowed: this endpoint refuses a self-review with 403 and a
    // second review with 409, and both are things the reviewer needs to read.
    fail(error);
  }
}

function propose(input: AlertRuleProposalInput) {
  if (LIVE) {
    void proposeLive(input);
    return;
  }
  counter += 1;
  const proposal: AlertRuleProposal = {
    proposalId: `PRP-${counter}`,
    status: "PENDING",
    alertRuleId: input.alertRuleId,
    scope: input.scope,
    officeScope: input.officeScope,
    notificationRecipients: input.notificationRecipients,
    active: input.active,
    reason: input.reason,
    proposedByRole: "Credit Policy",
    reviewedAt: null,
    reviewNotes: null,
  };

  // A newer proposal against the same rule supersedes any pending sibling.
  const proposals = state.proposals.map((p) =>
    p.status === "PENDING" && p.alertRuleId && p.alertRuleId === input.alertRuleId
      ? { ...p, status: "SUPERSEDED" as const, reviewedAt: iso(0), reviewNotes: "Superseded by a newer proposal." }
      : p,
  );

  state = { ...state, proposals: [proposal, ...proposals] };
  emit();
}

function review(proposalId: string, decision: "APPROVED" | "REJECTED", notes: string) {
  if (LIVE) {
    void reviewLive(proposalId, decision, notes);
    return;
  }
  const proposal = state.proposals.find((p) => p.proposalId === proposalId);
  if (!proposal) return;

  const proposals = state.proposals.map((p) =>
    p.proposalId === proposalId
      ? { ...p, status: decision, reviewedAt: iso(0), reviewNotes: notes }
      : p,
  );

  let activeRules = state.activeRules;
  if (decision === "APPROVED") {
    if (proposal.alertRuleId) {
      activeRules = activeRules.map((rule) =>
        rule.alertRuleId === proposal.alertRuleId
          ? {
              ...rule,
              scope: proposal.scope,
              officeScope: proposal.officeScope,
              notificationRecipients: proposal.notificationRecipients,
              active: proposal.active,
            }
          : rule,
      );
    } else {
      counter += 1;
      activeRules = [
        ...activeRules,
        {
          alertRuleId: `ALR-${String(counter).padStart(3, "0")}`,
          scope: proposal.scope,
          officeScope: proposal.officeScope,
          notificationRecipients: proposal.notificationRecipients,
          active: proposal.active,
          lastTriggeredAt: null,
        },
      ];
    }
  }

  state = { activeRules, proposals, error: null, isPending: false };
  emit();
}

export function useAlertRulesStore() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  // One fetch for the module, not one per mount. The `hydrated` guard is what keeps a
  // remount (navigating away and back) from re-fetching, while still ensuring the first
  // mount in live mode does. Mutations re-hydrate from their own responses.
  useEffect(() => {
    if (LIVE && !hydrated) {
      hydrated = true;
      void hydrate();
    }
  }, []);

  return {
    activeRules: snapshot.activeRules,
    proposals: snapshot.proposals,
    /** Present in live mode: why the last propose or review did nothing. */
    error: snapshot.error,
    isPending: snapshot.isPending,
    propose: useCallback((input: AlertRuleProposalInput) => propose(input), []),
    review: useCallback(
      (proposalId: string, decision: "APPROVED" | "REJECTED", notes: string) =>
        review(proposalId, decision, notes),
      [],
    ),
  };
}
